<template>
    <ModalDialogContainer ref="modalContainer" v-model:display="displayStatus">
        <div class="modal-dialog modal-lg" role="document" @click="stopPropagationEvent">
            <div class="modal-header">
                <div class="modal-title">
                    <span class="modal-info">{{ $t("Participant details") }}</span>
                    <span>{{ name ? name : $t("Data space participant") }}</span>
                </div>
                <button type="button" class="modal-close-btn" :title="$t('Close')" @click="close">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="modal-body">
                <div class="table-responsive">
                    <table class="table">
                        <tr>
                            <td class="bold">{{ $t("ID") }}</td>
                            <td class="text-right on-line">
                                <router-link
                                    v-if="(participantId + '').startsWith('did:')"
                                    class="link"
                                    to="#"
                                    @click.prevent="viewDidDocument"
                                    :title="$t('DID Document')"
                                >
                                    <i class="fa-solid fa-eye"></i>
                                    <span>{{ participantId || "-" }}</span>
                                </router-link>
                            </td>
                        </tr>
                        <tr>
                            <td class="bold">{{ $t("Connector URL") }}</td>

                            <td v-if="loading"><i class="fa fa-spinner fa-spin"></i> {{ $t("Loading") }}...</td>
                            <td v-else>{{ url }}</td>

                            <td></td>
                        </tr>
                    </table>
                </div>

                <div class="container">
                    <div class="container-header">
                        <h3>{{ $t("Attributes") }}</h3>
                        <button
                            v-if="firstAttestationIndex !== null"
                            type="button"
                            class="btn btn-secondary btn-xs"
                            @click="viewAttestation(firstAttestationIndex)"
                            :title="$t('Attestation')"
                        >
                            <span>{{ $t("View certificate") }}</span>
                        </button>
                    </div>
                    <div class="table-responsive">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>{{ $t("Attribute") }}</th>
                                    <th>{{ $t("Value") }}</th>
                                </tr>
                            </thead>
                            <tr v-for="(a, i) in attributes" :key="i">
                                <td>{{ a.name }}</td>
                                <td>{{ a.value }}</td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <DidDocumentModal
            v-if="displayDidDocumentModal"
            v-model:display="displayDidDocumentModal"
            :did="participantId || ''"
        ></DidDocumentModal>

        <AttestationViewModal
            v-if="displayAttestationModal"
            v-model:display="displayAttestationModal"
            :attestation="selectedAttestation"
            :subject="participantId"
            :subject-name="name"
        ></AttestationViewModal>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "../../utils/v-model";
import { getUniqueStringId } from "@/utils/unique-id";
import { Timeouts } from "@/utils/timeout";
import { ApiDataSpaceParticipants } from "@/api/api-group-data-space-participants";
import { DataSpaceKnownParticipantAttestation, DataSpaceKnownParticipantAttribute } from "@/api/definitions";
import { Request } from "@asanrom/request-browser";
import { AuthController } from "@/control/auth";
import AttestationViewModal from "./AttestationViewModal.vue";
import DidDocumentModal from "./DidDocumentModal.vue";

type DataSpaceKnownParticipantAttributeExtended = DataSpaceKnownParticipantAttribute & {
    attestation: number;
};

function mapAttributes(
    attributes: DataSpaceKnownParticipantAttribute[],
    attestations: DataSpaceKnownParticipantAttestation[],
): DataSpaceKnownParticipantAttributeExtended[] {
    const attMap = new Map<string, number>();
    attestations.forEach((a, i) => {
        a.attributes.forEach((atr) => {
            attMap.set(atr.name, i);
        });
    });

    return attributes.map((a) => {
        let attestation = -1;

        if (attMap.has(a.name)) {
            attestation = attMap.get(a.name);
        }

        return {
            name: a.name,
            value: a.value,
            attestation: attestation,
        };
    });
}

export default defineComponent({
    components: {
        AttestationViewModal,
        DidDocumentModal,
    },
    name: "DataSpaceParticipantModal",
    emits: ["update:display", "filter-by"],
    props: {
        display: Boolean,
        participantId: String,
        allowFilter: Boolean,
    },
    setup(props) {
        return {
            loadRequestId: getUniqueStringId(),
            displayStatus: useVModel(props, "display"),
            updateRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            loading: true,

            name: "",
            url: "",

            canUpdate: AuthController.hasPermission("participants.admin"),
            busyUpdating: false,

            attributes: [] as DataSpaceKnownParticipantAttributeExtended[],
            attestations: [] as DataSpaceKnownParticipantAttestation[],

            selectedAttestation: null as DataSpaceKnownParticipantAttestation,
            displayAttestationModal: false,

            displayDidDocumentModal: false,
        };
    },
    computed: {
        firstAttestationIndex(): number | null {
            return this.attributes.find((a) => a.attestation >= 0)?.attestation ?? null;
        },
    },
    methods: {
        load: function () {
            Timeouts.Abort(this.loadRequestId);

            this.loading = true;
            this.attributes = [];

            Request.Pending(this.loadRequestId, ApiDataSpaceParticipants.GetKnownParticipant(this.participantId))
                .onSuccess((res) => {
                    this.loading = false;

                    this.name = res.name;
                    this.url = res.url;
                    this.attributes = mapAttributes(res.attributes, res.attestations);
                    this.attestations = res.attestations;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        notFound: () => {
                            this.name = "";
                            this.url = "";
                            this.attributes = [];
                            this.loading = false;
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },

        close: function () {
            this.$closeModal();
        },

        escapeToClose: function (event) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        viewAttestation: function (i: number) {
            const att = this.attestations[i];

            if (!att) {
                return;
            }

            this.selectedAttestation = att;
            this.displayAttestationModal = true;
        },

        viewDidDocument: function () {
            this.displayDidDocumentModal = true;
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", () => {
            this.canUpdate = AuthController.hasPermission("participants.admin");
        });

        if (this.display) {
            this.$autoFocus();
            this.load();
        }
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
        Request.Abort(this.updateRequestId);
    },
    watch: {
        display: function () {
            if (this.display) {
                this.$autoFocus();
                this.load();
            }
        },
    },
});
</script>

<style scoped>
.modal-body {
    padding-top: 10px !important;
}

.table-responsive {
    border: none;
}

tr {
    border-top: var(--border-width-base) solid var(--color-border-neutral-subtle);
}

td {
    border-bottom: none !important;
}

.link {
    display: flex;
    align-items: center;
    gap: 6px;

    color: var(--color-text-primary-brand-default);

    font-size: var(--text-sm);
    font-weight: 600;

    text-decoration: none;
}

.container {
    display: inline-flex;
    flex-direction: column;
    gap: var(--space-1);
}

.container-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

h3 {
    font-size: var(--text-sm);
}

.btn-xs {
    height: 30px;
    padding: 0 var(--space-2);
    border-radius: var(--radius-button);
    border: var(--border-width-base) solid var(--color-border-default) !important;

    & span {
        color: var(--color-text-default);

        font-family: var(--font-family);
        font-size: var(--text-xs);
        font-style: normal;
        font-weight: 600;
        line-height: var(--leading-4);
    }
}

thead,
thead th,
thead tr {
    background-color: white !important;
    border: none !important;
    color: var(--color-text-neutral-subtle) !important;
}

.btn-mr {
    border-radius: var(--radius-button);
}
</style>
